import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

// Stable empty reference: `?? []` would hand a new array to TaskDetails on every render.
const NO_EXECUTIONS: TaskExecution[] = [];
import { Task, TaskExecution, TaskFilters, UpdateTaskRequest, ProvisioningStatus } from '../../types/task';
import { StatusIndicator, StatusType } from '../shared/StatusIndicators';
import { ResourceStatusBadge, StatusBadge } from '../shared/StatusBadge';
import { Button } from '../shared/Button';
import { useTaskManagement } from '../../hooks/useTaskManagement';
import { usePartnerData } from '../../hooks/usePartnerData';
import { useConnectionData } from '../../hooks/useConnectionData';
import { TaskDetails } from './TaskDetails';
import { TaskCreator } from './TaskCreator';
import { TaskEditor } from './TaskEditor';
import { HeldFilesReview } from './HeldFilesReview';
import { tasksApi } from '../../services/api/tasks';
import { RoleGuard } from '../RoleGuard';
// THUB-186: shared timezone-aware relative time; replaces a third local copy.
import { formatRelativeTime as sharedRelativeTime } from '../../utils/formatDateTime';
import { useTimezone } from '../../contexts/TimezoneContext';

/**
 * Returns true if the task has no valid partner or connection name,
 * indicating it references a deleted or missing parent resource.
 */
/**
 * The provisioning values the Provisioning dropdown offers, and therefore the only ones the
 * `?filter=` deep link may set. Keep in step with the <option> list in the filter bar below.
 */
const PROVISIONING_FILTER_VALUES: readonly string[] = [
  'active',
  'provisioning',
  'pending',
  'failed',
  'deleting',
  'orphaned',
];

function isOrphanedTask(task: Task): boolean {
  const invalidNames = [null, undefined, '', 'Unknown'];
  const partnerInvalid = invalidNames.includes(task.partnerName as any);
  const connectionInvalid = invalidNames.includes(task.connectionName as any);
  return partnerInvalid || connectionInvalid;
}

interface TaskListProps {
  onTaskSelect?: (task: Task) => void;
}

function TaskList({ onTaskSelect }: TaskListProps) {
  // THUB-186: subscribe so a timezone change re-renders the relative times below.
  const { timezone } = useTimezone();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  // THUB-152: taskId whose held files are being reviewed before enabling (null = closed).
  const [heldReviewTaskId, setHeldReviewTaskId] = useState<string | null>(null);

  // React Router deep linking
  const { taskId: urlTaskId } = useParams<{ taskId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // The provisioning filter LIVES IN THE URL. It is not mirrored into `filters` state.
  //
  // Three shapes have been tried here and the first two were both wrong:
  //  1. an effect copying `?filter=orphaned` into state on every searchParams change;
  //  2. seeding state from the URL in the useState initializer below.
  // (2) broke the browser Back button: `/tasks` and `/tasks/:taskId` share this component, so a
  // same-route URL change does not remount it, and an initializer runs once per MOUNT. After
  // Back, the address bar read ?filter=orphaned while the table listed the healthy tasks and the
  // dropdown read "All Provisioning" -- an operator would conclude there were no orphaned tasks.
  //
  // Deriving it from the URL instead means there is only ever ONE source of truth, so Back,
  // Forward, refresh, a bookmark, an in-app link and the dropdown cannot disagree by
  // construction. The dropdown writes the URL (replace, so it does not pile up history entries)
  // and re-reads it on the next render.
  //
  // Correcting the record: the effect in (1) was NOT removed because it "reinstated the filter
  // after the operator cleared it". Its deps were [searchParams], and clearing the dropdown never
  // touched the URL, so it never re-ran -- that bug did not exist. It was removed for the lint
  // finding, and seeding was a bad substitute for it.
  // Only values the dropdown actually offers count. The tst baseline compared the URL against the
  // single literal 'orphaned', so an unrecognised value was simply ignored and the operator saw the
  // normal list; taking the raw value instead turned `?filter=bogus` (a typo, a stale bookmark, a
  // hand-edited URL) into a filter that matches nothing -- an empty table with a blank Provisioning
  // dropdown and no explanation. Ignoring what we cannot honour keeps that behaviour.
  const rawFilter = searchParams.get('filter') ?? '';
  const provisioningFilter = PROVISIONING_FILTER_VALUES.includes(rawFilter) ? rawFilter : '';
  const provisioningStatus = useMemo(
    () => (provisioningFilter ? [provisioningFilter as any] : []),
    [provisioningFilter]
  );
  const setProvisioningFilter = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set('filter', value);
      else next.delete('filter');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Filters
  // THUB-298: lazy initializer. As an eager literal this rebuilt the object -- and called
  // Date.now()/new Date() -- on every render, even though useState discards it after mount.
  // Reading the clock during render is impure (react-hooks/purity); this computes the default
  // window once, at mount, which is what the code always meant.
  const [filters, setFilters] = useState<TaskFilters>(() => ({
    status: [],
    partners: [],
    connections: [],
    transferDirections: [],
    scheduleTypes: [],
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      end: new Date()
    }
  }));

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting
  const [sortField, setSortField] = useState<keyof Task>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Get data from individual hooks
  const { tasks, loading: tasksLoading, error: tasksError, refreshTasks } = useTaskManagement();
  const { partners } = usePartnerData();
  const { connections } = useConnectionData();

  // THUB-299: selection is DERIVED from the route, not duplicated into state.
  //
  // The route is already the source of truth -- every open path calls navigate(`/tasks/:id`) --
  // so a `selectedTask` useState was a second copy that had to be re-synced by effect whenever
  // the tasks list changed. Deriving it also closes a latent inconsistency: two close paths
  // (after an update, after a delete) used to set selectedTask to null WITHOUT navigating, so the
  // URL still named an open task and a refresh reopened it. Now closing means navigating, and the
  // two cannot disagree.
  //
  // A URL naming a task that no longer exists derives to null, which is exactly what the old
  // effect's `else if (!updatedTask)` branch did by hand.
  const selectedTask: Task | null = urlTaskId
    ? tasks.find(t => t.taskId === urlTaskId) ?? null
    : null;

  // Opening or closing a task is NOT a filter change, so it must carry the query string along.
  // Now that the provisioning filter lives in the URL, a bare navigate('/tasks') silently drops
  // it: the operator arrives from the Dashboard's orphaned worklist, opens one to investigate, and
  // the list behind the modal switches to the healthy tasks -- then closing it lands on the plain
  // view, which HIDES orphaned tasks, so the worklist they were working through disappears.
  // Both tst and the previous state-held shape preserved it, because state survived a same-route
  // navigation. Preserving `search` is what makes URL-as-source-of-truth equivalent.
  const closeTaskDetails = useCallback(() => {
    navigate({ pathname: '/tasks', search: location.search });
  }, [navigate, location.search]);

  // Task detail read, for integration data only. Keyed under tasks.detail so a task broadcast
  // reaches it. A failure is tolerated: integration data is optional and its absence must not
  // stop an operator opening the task, which is what the original's `.catch(() => {})` meant.
  const taskDetailQuery = useQuery({
    queryKey: queryKeys.tasks.detail(selectedTask?.taskId ?? ''),
    queryFn: () => tasksApi.getTask(selectedTask!.taskId),
    enabled: !!selectedTask,
  });
  const taskIntegration = taskDetailQuery.data?.integration;

  // THUB-161: the History tab and Overview metrics were never given execution data (TaskDetails
  // defaulted executions=[]), so they showed "0 executions" even for tasks that had run. Overview
  // derives its counters from these; task.metrics is the persisted rollup, fixed on the backend.
  const executionsQuery = useQuery({
    queryKey: queryKeys.tasks.recentExecutions(selectedTask?.taskId ?? '', 50),
    queryFn: () => tasksApi.getExecutionsForTask(selectedTask!.taskId, 50),
    enabled: !!selectedTask,
  });
  const taskExecutions: TaskExecution[] = executionsQuery.data ?? NO_EXECUTIONS;
  const executionsLoading = executionsQuery.isPending;

  // THUB-299: two effects used to live here, each with an exhaustive-deps disable -- one
  // re-syncing selectedTask whenever the tasks list changed ("intentionally omits selectedTask
  // from deps to avoid infinite loop when we update it"), one mapping the :taskId URL param onto
  // selectedTask on load. Both are gone: selection is DERIVED from the route above, so there is
  // no second copy to keep in sync and nothing for an effect to feed back into.
  //
  // Needing to hide a dependency from the linter to stop an effect feeding itself was the signal
  // that the state was duplicated rather than that the rule was wrong.

  // Detect if "orphaned" filter is active
  const isOrphanedFilterActive = useMemo(() => {
    return provisioningStatus.includes('orphaned' as any);
  }, [provisioningStatus]);

  // Filter, search, and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = [...tasks];

    // Apply orphaned filter logic:
    // - If "orphaned" filter is selected, show ONLY orphaned tasks
    // - Otherwise, hide orphaned tasks from the default view
    if (isOrphanedFilterActive) {
      result = result.filter(t => isOrphanedTask(t));
    } else {
      result = result.filter(t => !isOrphanedTask(t));
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(search) ||
        (t.description || '').toLowerCase().includes(search) ||  // THUB-160: search display label too
        (t.partnerName || '').toLowerCase().includes(search) ||
        (t.connectionName || '').toLowerCase().includes(search) ||
        t.status.toLowerCase().includes(search) ||
        t.transferDirection.toLowerCase().includes(search)
      );
    }

    // Apply filters
    if (filters.partners.length > 0) {
      result = result.filter(t => filters.partners.includes(t.partnerId));
    }
    if (filters.connections.length > 0) {
      result = result.filter(t => filters.connections.includes(t.connectionId));
    }
    if (filters.status.length > 0) {
      result = result.filter(t => filters.status.includes(t.status));
    }
    if (filters.transferDirections.length > 0) {
      result = result.filter(t => filters.transferDirections.includes(t.transferDirection));
    }
    // Apply provisioning status filter (skip if "orphaned" since handled above)
    if (provisioningStatus.length > 0 && !isOrphanedFilterActive) {
      result = result.filter(t => provisioningStatus.includes(t.provisioningStatus || 'active'));
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Number comparison
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return result;
  }, [tasks, searchTerm, filters, provisioningStatus, sortField, sortOrder, isOrphanedFilterActive]);

  // Task selection handler. Navigating IS selecting now -- the detail and executions reads are
  // driven by the derived selection, so there is nothing to kick off by hand here.
  const handleTaskSelect = useCallback((task: Task) => {
    // Carries `search` for the same reason as closeTaskDetails above.
    navigate({ pathname: `/tasks/${task.taskId}`, search: location.search });
    onTaskSelect?.(task);
  }, [onTaskSelect, navigate, location.search]);

  // Handle create task
  const handleCreateTask = useCallback(async (taskData: any) => {
    // THUB-142: forward whatever the wizard emitted. TaskCreator already gates
    // each field behind type-specific spreads (delivery*, http*, postPickup*),
    // so unrelated fields are simply absent rather than wrong.
    // archiveAccessEnabled is a UI-only field with no backend handling — drop it.
    const { archiveAccessEnabled, ...payload } = taskData;
    try {
      await tasksApi.createTask(payload);
      setShowCreateModal(false);
      toast.success('Task is being set up');
      refreshTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task. Please try again.');
    }
  }, [refreshTasks]);

  // Handle validate configuration
  const handleValidateConfiguration = useCallback(async (transferDirection: any, configuration: any) => {
    try {
      const result = await tasksApi.validateTaskConfiguration(transferDirection, configuration);
      return result;
    } catch (error) {
      console.warn('Configuration validation failed (likely CORS issue):', error);
      // Return a warning instead of blocking task creation
      return {
        valid: true,
        errors: ['Configuration validation unavailable due to network issues - task will be created without validation']
      };
    }
  }, []);

  // THUB-152: finish an enable once a selection is known (or when nothing is held).
  // `selectedHeldFileIds === undefined` means "no selection was made" and OMITS the key,
  // so the backend flushes every eligible held file — identical to pre-THUB-152 behavior.
  const completeEnable = useCallback(async (taskId: string, selectedHeldFileIds?: string[]) => {
    const result = await tasksApi.enableTask(taskId, true, selectedHeldFileIds);
    // THUB-152: unselected files are DISCARDED (removed from the delivery queue), not left
    // queued. Say "removed from the queue" rather than "discarded" — and always pair it with
    // the archive statement, because an operator reading "discarded" reasonably fears the
    // file was destroyed. It was not: only the queued delivery is gone.
    const discarded = result.discardedNotSelectedCount ?? 0;
    const removed = discarded
      ? `${discarded} removed from the delivery queue (still in the archive)`
      : '';
    if (result.flushedCount) {
      toast.success(
        `Task enabled — delivering ${result.flushedCount} held file(s)`
        + (removed ? `; ${removed}` : '')
      );
    } else if (discarded) {
      toast.success(`Task enabled — no files delivered; ${removed}`);
    } else {
      toast.success('Task enabled');
    }
    // A row that could not be removed is still queued; never let that pass as a clean run.
    if (result.errorCount) {
      toast.error(
        `${result.errorCount} held file(s) could not be removed from the queue and are still held`
      );
    }
    refreshTasks();
  }, [refreshTasks]);

  // Handle toggle task status (enable/disable)
  const handleToggleTaskStatus = useCallback(async (taskId: string, enabled: boolean) => {
    try {
      if (enabled) {
        // THUB-149: probe first — if files were held while disabled, the backend asks
        // for confirmation before delivering them (does NOT enable on the probe).
        const probe = await tasksApi.enableTask(taskId);
        if (probe.requiresConfirmation) {
          // THUB-152: open the held-file review instead of the old all-or-nothing
          // window.confirm. The enable is finished from the modal's onConfirm, so we
          // return here WITHOUT refreshing — the task is still disabled until then.
          setHeldReviewTaskId(taskId);
          return;
        }
        toast.success('Task enabled');
      } else {
        await tasksApi.disableTask(taskId);
      }
      // Refresh task list to reflect the status change
      refreshTasks();
    } catch (error) {
      console.error('Failed to toggle task status:', error);
      toast.error('Failed to update task status');
    }
  }, [refreshTasks]);

  // Handle execute task
  const handleExecuteTask = useCallback(async (taskId: string) => {
    try {
      await tasksApi.executeTaskManually(taskId);
      // No re-sync needed: selectedTask is derived from `tasks`, so the next refresh of the
      // list is picked up automatically. The old code copied the updated row into state here.
    } catch (error) {
      console.error('Failed to execute task:', error);
    }
    // selectedTask / tasks are no longer read here: the derived selection picks up the refreshed
    // row on its own, so copying it in was the only thing that needed them.
  }, []);

  // Handle edit task - open edit modal
  const handleEditTask = useCallback((task: Task) => {
    console.log('Edit task:', task);
    setTaskToEdit(task);
    setShowEditModal(true);
  }, []);

  // Handle update task
  const handleUpdateTask = useCallback(async (taskId: string, taskData: UpdateTaskRequest) => {
    try {
      await tasksApi.updateTask(taskId, taskData);
      setShowEditModal(false);
      setTaskToEdit(null);
      // Close task details modal if it's the same task
      if (selectedTask && selectedTask.taskId === taskId) {
        // Navigate rather than nulling state: the URL is the source of truth, and leaving it
        // pointing at a closed task is what made a refresh reopen it.
        closeTaskDetails();
      }
      toast.success('Task update in progress');
      // Auto-refresh to show updated task data
      refreshTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error; // Let the TaskEditor handle the error display
    }
  }, [selectedTask, refreshTasks, closeTaskDetails]);

  // Delete state
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Handle delete task
  const handleDeleteTask = useCallback(async (taskId: string) => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const taskToDelete = tasks.find(t => t.taskId === taskId);
      await tasksApi.deleteTask(taskId, { version: taskToDelete?.version });
      closeTaskDetails(); // Close modal (clears the :taskId from the URL too)
      toast.success('Task deletion in progress');
      // Auto-refresh to update list
      refreshTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete task. Please try again.';
      setDeleteError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [tasks, refreshTasks, closeTaskDetails]);

  // Optimized sort handler
  const handleSort = useCallback((field: keyof Task) => {
    setSortField(prevField => {
      if (prevField === field) {
        setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortOrder('asc');
      }
      return field;
    });
  }, []);

  // Memoized status color function for task administrative status (enabled/disabled)
  const getStatusColor = useCallback((status: string): StatusType => {
    switch (status) {
      case 'enabled': return 'connected';
      case 'disabled': return 'neutral';
      default: return 'info';
    }
  }, []);

  // T053: Helper to get provisioning status from task
  const getProvisioningStatus = useCallback((task: Task): ProvisioningStatus => {
    return task.provisioningStatus || 'active';
  }, []);

  // Format relative time for last execution.
  //
  // THUB-186: a THIRD local copy of this logic (the audit found two), carrying the same
  // THUB-272 bug — `new Date(isoDate)` on a NAIVE backend timestamp parses as LOCAL time
  // and future-dates it, so diffMins went negative, `diffMins < 1` matched, and the Tasks
  // page rendered "just now" for everything. Observed live in NP on the Tasks list.
  const formatRelativeTime = useCallback((isoDate: string): string => {
    if (!isoDate) return '';
    return sharedRelativeTime(isoDate, timezone);
  }, [timezone]);

  // Memoized sort icon function
  const getSortIcon = useCallback((field: keyof Task) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  }, [sortField, sortOrder]);

  // Optimized clear filters handler
  const clearFilters = useCallback(() => {
    // The provisioning filter lives in the URL, so clearing has to clear it there too --
    // otherwise "Clear filters" leaves ?filter=... behind and a refresh reinstates it.
    setProvisioningFilter('');
    setFilters({
      status: [],
      partners: [],
      connections: [],
      transferDirections: [],
      scheduleTypes: [],
      dateRange: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        end: new Date()
      }
    });
    setSearchTerm('');
  }, [setProvisioningFilter]);

  // Memoized active filters check
  const hasActiveFilters = useMemo(() => {
    return searchTerm ||
      filters.partners.length > 0 ||
      filters.connections.length > 0 ||
      filters.status.length > 0 ||
      provisioningStatus.length > 0 ||
      filters.transferDirections.length > 0;
  }, [searchTerm, filters, provisioningStatus]);

  // Memoized task statistics (excludes orphaned tasks from counts)
  // Note: 'running' is now tracked via lastExecution.status, not task.status
  const nonOrphanedTasks = useMemo(() => tasks.filter(t => !isOrphanedTask(t)), [tasks]);
  const orphanedTaskCount = useMemo(() => tasks.filter(t => isOrphanedTask(t)).length, [tasks]);
  const taskStats = useMemo(() => ({
    total: nonOrphanedTasks.length,
    enabled: nonOrphanedTasks.filter(t => t.status === 'enabled').length,
    running: nonOrphanedTasks.filter(t => t.lastExecution?.status === 'running').length,
    orphaned: orphanedTaskCount
  }), [nonOrphanedTasks, orphanedTaskCount]);

  if (tasksLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-neutral-500 dark:text-slate-400">Loading tasks...</div>
      </div>
    );
  }

  if (tasksError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <h3 className="text-lg font-semibold text-danger-600 dark:text-danger-400">Error Loading Tasks</h3>
        <p className="text-neutral-600 dark:text-slate-400">{tasksError}</p>
        <Button variant="primary" onClick={refreshTasks}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white">
            Tasks
          </h2>
          <p className="text-sm text-neutral-500 dark:text-slate-400 mt-0.5">
            Manage tasks and monitor execution status
          </p>
        </div>
        <RoleGuard minimumRole="admin">
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create Task
          </Button>
        </RoleGuard>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="
              w-full px-4 py-2.5
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-lg text-sm
              text-neutral-900 dark:text-slate-100
              placeholder:text-neutral-400 dark:placeholder:text-slate-500
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          />
          {searchTerm && (
            <button
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                w-5 h-5 flex items-center justify-center
                text-neutral-400 hover:text-neutral-600
                dark:text-slate-500 dark:hover:text-slate-300
              "
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={filters.partners[0] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({
                ...prev,
                partners: value ? [value] : []
              }));
            }}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Partners</option>
            {partners.map(partner => (
              <option key={partner.partnerId} value={partner.partnerId}>
                {partner.name}
              </option>
            ))}
          </select>

          <select
            value={filters.connections[0] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({
                ...prev,
                connections: value ? [value] : []
              }));
            }}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Connections</option>
            {connections.map(connection => (
              <option key={connection.connectionId} value={connection.connectionId}>
                {connection.name}
              </option>
            ))}
          </select>

          <select
            value={filters.status[0] || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({
                ...prev,
                status: value ? [value as any] : []
              }));
            }}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Statuses</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>

          <select
            value={provisioningFilter}
            onChange={(e) => setProvisioningFilter(e.target.value)}
            className="
              px-3 py-2.5 rounded-lg text-sm
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              text-neutral-700 dark:text-slate-300
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Provisioning</option>
            <option value="active">Active</option>
            <option value="provisioning">Provisioning</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="deleting">Deleting</option>
            <option value="orphaned">Orphaned</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="
                px-3 py-2.5 rounded-lg text-sm
                text-neutral-600 dark:text-slate-400
                hover:text-neutral-900 dark:hover:text-slate-100
                hover:bg-neutral-100 dark:hover:bg-slate-700
                transition-colors duration-150
              "
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Filter Results */}
      {hasActiveFilters && (
        <div className="text-sm text-neutral-600 dark:text-slate-400">
          {isOrphanedFilterActive
            ? `Showing ${filteredAndSortedData.length} orphaned tasks`
            : `Showing ${filteredAndSortedData.length} of ${taskStats.total} tasks`
          }
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 dark:bg-slate-800">
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('name')}
                >
                  Task Name {getSortIcon('name')}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('partnerName' as keyof Task)}
                >
                  Partner {getSortIcon('partnerName' as keyof Task)}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('connectionName' as keyof Task)}
                >
                  Connection {getSortIcon('connectionName' as keyof Task)}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('transferDirection')}
                >
                  Direction {getSortIcon('transferDirection')}
                </th>
                <th
                  className="px-4 py-3.5 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap transition-colors"
                  onClick={() => handleSort('status')}
                >
                  Status {getSortIcon('status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-neutral-500 dark:text-slate-400 italic">
                    {hasActiveFilters ? 'No tasks match your filters' : 'No tasks found'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map(task => (
                  <tr
                    key={task.taskId}
                    onClick={() => handleTaskSelect(task)}
                    className="
                      border-b border-neutral-100 dark:border-slate-700/50
                      hover:bg-primary-50 dark:hover:bg-primary-900/20
                      cursor-pointer transition-colors duration-150
                    "
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-neutral-900 dark:text-slate-100">{task.name}</span>
                          {task.pgpEnabled && (
                            <span className="
                              inline-flex items-center gap-1
                              px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide
                              bg-primary-100 dark:bg-primary-900/30
                              text-primary-700 dark:text-primary-300
                              border border-primary-200 dark:border-primary-800
                            ">
                              <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                              PGP
                            </span>
                          )}
                        </div>
                        {/* THUB-160: show the human-readable display label under the
                            name (name stays the primary id since it's the S3 prefix). */}
                        {task.description && (
                          <span className="text-xs text-neutral-600 dark:text-slate-300">{task.description}</span>
                        )}
                        {task.configuration?.sourceDirectory && (
                          <span className="text-xs text-neutral-500 dark:text-slate-400">{task.configuration.sourceDirectory}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-neutral-700 dark:text-slate-300">
                      <span>{task.partnerName || 'Unknown'}</span>
                      {isOrphanedTask(task) && (
                        <StatusBadge variant="danger" size="sm" className="ml-2">
                          Orphaned
                        </StatusBadge>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-neutral-700 dark:text-slate-300">
                      {task.connectionName || 'Unknown'}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="
                        inline-block px-2.5 py-1 rounded-full
                        text-xs font-medium uppercase tracking-wide
                        bg-neutral-100 dark:bg-slate-700
                        text-neutral-700 dark:text-slate-300
                      ">
                        {task.transferDirection}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <ResourceStatusBadge
                            status={getProvisioningStatus(task)}
                            size="sm"
                          />
                          <StatusIndicator
                            status={getStatusColor(task.status)}
                            label={task.status}
                            size="small"
                          />
                        </div>
                        {(getProvisioningStatus(task) === 'active') && (
                          <div className="text-xs">
                            {task.lastExecution?.status === 'completed' ? (
                              <span className="text-success-600 dark:text-success-400">
                                Succeeded {task.lastExecution.startTime ? formatRelativeTime(task.lastExecution.startTime) : ''}
                              </span>
                            ) : task.lastExecution?.status === 'failed' ? (
                              <span
                                className="text-danger-600 dark:text-danger-400"
                                title={task.lastExecution.errorMessage || undefined}
                              >
                                Failed {task.lastExecution.startTime ? formatRelativeTime(task.lastExecution.startTime) : ''}
                                {task.lastExecution.errorMessage && (
                                  <span className="block text-danger-500/80 dark:text-danger-400/70 truncate max-w-[220px]">
                                    {task.lastExecution.errorMessage}
                                  </span>
                                )}
                              </span>
                            ) : task.lastExecution?.status === 'running' ? (
                              <span className="text-primary-600 dark:text-primary-400 animate-pulse">
                                Running
                              </span>
                            ) : (
                              <span className="text-neutral-400 dark:text-slate-500">No executions</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredAndSortedData.length > 0 && (
          <div className="px-4 py-3 bg-neutral-50 dark:bg-slate-800 border-t border-neutral-200 dark:border-slate-700">
            <span className="text-sm text-neutral-500 dark:text-slate-400">
              {isOrphanedFilterActive
                ? `Showing ${filteredAndSortedData.length} orphaned tasks`
                : hasActiveFilters
                  ? `Showing ${filteredAndSortedData.length} of ${taskStats.total} tasks`
                  : `${filteredAndSortedData.length} tasks`
              }
            </span>
          </div>
        )}
      </div>

      {/* Task Details Modal */}
      {selectedTask && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1000] p-4"
          onClick={closeTaskDetails}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[1200px] max-h-[90vh]
              overflow-hidden flex flex-col
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <TaskDetails
              task={taskIntegration ? { ...selectedTask, integration: taskIntegration } : selectedTask}
              executions={taskExecutions}
              loading={executionsLoading}
              onClose={() => {
                closeTaskDetails();
                refreshTasks();
              }}
              onToggleStatus={handleToggleTaskStatus}
              onExecute={handleExecuteTask}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              isDeleting={isDeleting}
              deleteError={deleteError}
            />
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1000] p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[900px] max-h-[90vh]
              overflow-hidden
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <TaskCreator
              partners={partners.map(p => ({ id: p.partnerId, name: p.name }))}
              onSave={handleCreateTask}
              onCancel={() => setShowCreateModal(false)}
              onValidateConfiguration={handleValidateConfiguration}
            />
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && taskToEdit && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1000] p-4"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[900px] max-h-[90vh]
              overflow-hidden
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <TaskEditor
              task={taskToEdit}
              partners={partners.map(p => ({ id: p.partnerId, name: p.name }))}
              onSave={handleUpdateTask}
              onCancel={() => {
                setShowEditModal(false);
                setTaskToEdit(null);
              }}
              onValidateConfiguration={handleValidateConfiguration}
            />
          </div>
        </div>
      )}

      {/* THUB-152: Held-file review before enabling. Replaces the all-or-nothing confirm.
          No overlay-click-to-close: the operator is mid-decision about files that go to a
          partner, and a stray click dismissing that is the wrong default. */}
      {heldReviewTaskId && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1000] p-4">
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[900px] max-h-[90vh]
              overflow-hidden
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <HeldFilesReview
              taskId={heldReviewTaskId}
              taskName={tasks.find(t => t.taskId === heldReviewTaskId)?.name}
              onCancel={() => setHeldReviewTaskId(null)}
              onConfirm={async (selectedHeldFileIds) => {
                const taskId = heldReviewTaskId;
                try {
                  await completeEnable(taskId, selectedHeldFileIds);
                  setHeldReviewTaskId(null);
                } catch (error) {
                  console.error('Failed to enable task with held-file selection:', error);
                  toast.error('Failed to enable task');
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskList;
